import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getSheet } from "../sheets";
import {
  ageGroups,
  colors,
  female,
  getAge,
  getPersonality,
  male
} from "../utils";
import AgeChart from "./AgeChart";
import Container from "./Container";
import GenderChart from "./GenderChart";
import Loader from "./Loader";

let colorUsages = 0;
const Color = styled.span`
  background: ${() => {
    const color = colors[colorUsages % colors.length];
    colorUsages += 1;
    return `${color}55`;
  }};

  color: black;
  display: inline-block;
  padding: 0 0.33rem;
`;

const dataSheet = process.env.REACT_APP_DATA_SHEET;

const parseDate = stringDate => {
  const [day, month, year] = stringDate.split("/");

  return new Date(year, month, day);
};

const Data = () => {
  const [data, setData] = useState();

  useEffect(() => {
    const fetchData = async () => {
      await getSheet(dataSheet, "Schedaggio!C6:T", response => {
        const mapped = response.result.values.map(
          ([
            name,
            nickname,
            gender,
            job,
            from,
            twitter,
            instagram,
            youtube,
            younow,
            goodreads,
            dob,
            age,
            height,
            personality
          ]) => ({
            gender,
            dob,
            age: dob ? getAge(parseDate(dob)) : undefined,
            height: parseInt(height),
            personality: getPersonality(personality),
            from
          })
        );

        setData(mapped);
      });
    };

    fetchData();
  }, []);

  return (
    <div>
      {data ? (
        (() => {
          const heights = data.map(({ height }) => height).filter(Boolean);

          const averageHeight = (
            heights.reduce((a, b) => a + b, 0) / heights.length
          ).toFixed(0);

          const ages = data
            .map(({ age }) => age)
            .filter(Boolean)
            .map(age => parseInt(age));

          const averageAge = (
            ages.reduce((a, b) => a + b, 0) / ages.length
          ).toFixed(0);

          const distribution = data.reduce(
            (a, { gender }) => {
              if (gender === "m") a[male] += 1;
              else if (gender === "f") a[female] += 1;
              else {
                if (!a[gender]) a[gender] = 0;
                a[gender] += 1;
              }

              return a;
            },
            {
              [male]: 0,
              [female]: 0
            }
          );

          const distributionForChart = Object.entries(distribution)
            .map(([name, value]) => ({
              name,
              value
            }))
            .sort((a, b) => {
              return a.value < b.value ? 1 : -1;
            });

          const mostFrequentGender = distributionForChart.reduce((a, b) => {
            return a.value > b.value ? a : b;
          }).name;

          const birthdayFrequency = data
            .filter(({ dob }) => !!dob)
            .reduce((dates, { dob }) => {
              const [day, month] = dob.split("/");
              const date = `${day}/${month}`;
              const found = dates.find(e => e.date === date);

              if (!found)
                dates.push({
                  date,
                  value: 1
                });
              else found.value += 1;

              return dates;
            }, []);

          const mostFrequentBirthdayValue = Math.max(
            ...birthdayFrequency.map(({ value }) => value)
          );

          const mostFrequentBirthdays = birthdayFrequency
            .filter(e => e.value === mostFrequentBirthdayValue)
            .sort((a, b) => a.date.split("/")[1] - b.date.split("/")[1])
            .map(({ date }) => {
              const [day, month] = date.split("/");
              const actualDate = new Date(new Date().getFullYear(), month, day);

              const dateString = actualDate.toLocaleDateString("it-IT", {
                day: "numeric",
                month: "long"
              });

              // please don't make me convert this
              // eslint-disable-next-line eqeqeq
              if (day == 1 || day == 8) return `l'${dateString}`;
              return `il ${dateString}`;
            });

          const ageByGroups = ages.reduce(
            (groups, age) => {
              const group = groups.find(
                ({ min, max }) => age >= min && age < max
              );

              if (!group.value) group.value = 0;
              group.value += 1;

              return groups;
            },
            [...ageGroups]
          );

          const mostFrequentAgeGroup = (() => {
            const group = ageByGroups.reduce((a, b) => {
              return a.value > b.value ? a : b;
            });

            if (!group.min) {
              return `al di sotto dei ${group.max} anni`;
            }

            if (!group.max) {
              return `al di sopra dei ${group.min} anni`;
            }

            return `tra i ${group.min} e i ${group.max} anni`;
          })();

          const ageByGroupsForChart = ageByGroups.map(({ min, max, value }) => {
            if (min === -Infinity)
              return {
                name: `<${max}`,
                value
              };

            if (max === Infinity)
              return {
                name: `>${min}`,
                value
              };

            return {
              name: `${min}-${max}`,
              value
            };
          });

          return (
            <Container>
              <p>
                Al momento il prismino medio ha <Color>{averageAge}</Color> anni
                ed è alto circa <Color>{averageHeight}</Color>cm
              </p>
              <p>Siamo più o meno distribuiti così</p>
              <div>
                <GenderChart data={distributionForChart} />
              </div>
              <p>
                Perciò è più probabile si identifichi come{" "}
                <Color>{mostFrequentGender}</Color>
              </p>
              <p>
                Potrebbe spegnere le candeline{" "}
                {mostFrequentBirthdays.map((e, i) => {
                  if (i > 0)
                    return (
                      <React.Fragment key={e}>
                        o <Color key={e}>{e}</Color>
                      </React.Fragment>
                    );
                  return <Color key={e}>{e}</Color>;
                })}
                , dat{mostFrequentBirthdays.length > 1 ? "e" : "a"} in cui{" "}
                <Color>{mostFrequentBirthdayValue}</Color> di noi compiono gli
                anni!
              </p>
              <p>
                Parlando di età, siamo decisamente di più{" "}
                <Color>{mostFrequentAgeGroup}</Color>
              </p>
              <div>
                <AgeChart data={ageByGroupsForChart} />
              </div>
            </Container>
          );
        })()
      ) : (
        <Loader>Prisming</Loader>
      )}
    </div>
  );
};

export default Data;
